/**
 * The source image codes of an asset, as a badge narrow enough for a grid row.
 *
 * `AssetCard` and `BasketDrawer` both show lineage on a single `text-[11px]`
 * line already shared with duration and dimensions. Three codes spelled out
 * there — `SI-000042 SI-000043 SI-000044` — is thirty characters of
 * near-identical text that pushes the dimensions off the row and that nobody
 * reads at a glance, which is the only thing a grid badge is for.
 *
 * So the first code leads and the rest are counted. The count is the part that
 * matters: showing one source of three with no sign the others exist is the
 * bug this whole change exists to fix, just relocated to the display layer.
 * `+2` says "there are more" in two characters, and the full list is one hover
 * (the `title`) or one click (the detail overlay, which shows every thumbnail)
 * away.
 *
 * Returns null when there is nothing to show, so callers can render nothing
 * rather than an empty pill.
 */
export function sourceImageBadge(
  codes: readonly string[] | null | undefined,
): { label: string; title: string } | null {
  const present = codes ?? [];
  if (present.length === 0) return null;

  const [first, ...rest] = present;
  return {
    label: rest.length > 0 ? `${first} +${rest.length}` : first,
    // Every code, because the tooltip is where "which three?" gets answered
    // without leaving the grid. Singular/plural rather than "1 source image(s)".
    title: present.length === 1 ? `Source image: ${first}` : `Source images: ${present.join(', ')}`,
  };
}
